// Shared catalog for the driver-portal case questionnaire. The office ticks which of these
// to push (plus any custom questions); the selection is stored on the case as a self-describing
// list, so the driver portal renders it without needing this catalog. Kept here so the admin
// picker and any helper agree on ids/types.
//
// Types:
//   yesno       — Yes / No buttons
//   choice      — pick one of `options`
//   text        — a free-text box (always shown)
//   yesno_text  — Yes (reveals a text box) / No     [used by the always-appended closer]

export const CATALOG = [
  { id: 'recall', prompt: 'Do you recall this delivery?', type: 'yesno' },
  { id: 'usual_location', prompt: 'Where do you usually deliver at this address?', type: 'choice', options: ['Reception', 'Concierge', 'Mailroom', 'Front door'] },
  { id: 'where_located', prompt: 'Where is the {usual_location} located?', type: 'text', dependsOn: 'usual_location' },
  { id: 'level', prompt: 'On what level?', type: 'text' },
  { id: 'photo_recall', prompt: 'Does this photo jog your memory?', type: 'yesno', requiresPhoto: true },
];

// Always appended by the driver form — the office doesn't pick this one.
export const CLOSER = { id: 'additional_info', prompt: 'Any other detail about this delivery?', type: 'yesno_text' };

// A stable id for an office-typed custom question, derived from its text — so re-saving the
// questionnaire (adding one, removing another) never re-uses an id the driver already answered,
// which would silently swallow the new question.
export function customQuestionId(prompt) {
  const h = [...String(prompt || '')].reduce((a, ch) => ((a * 31 + ch.charCodeAt(0)) | 0), 7);
  return `custom_${Math.abs(h).toString(36)}`;
}

// A question the office typed itself must be answered. They are often instructions rather than
// questions ("Please search for this parcel today"), and as optional text boxes a driver could tap
// straight past them to Submit — case 73692968 (15 Sep 2026) went back to the office with both
// custom questions blank, which read as though they had never reached the portal.
export const isRequiredText = (q) => !!(q && q.type === 'text' && q.custom);

// The questions still owing an answer on this case: the office's list, minus photo questions when
// there's no photo, minus anything already answered. Non-empty after a submission means the office
// added a question afterwards — the driver gets a follow-up round rather than a dead "thanks" box.
// A blank answer to a required question is not an answer, so it comes back in the next round.
export function pendingQuestions(c) {
  const hasPhoto = ((c && c.images) || []).length > 0;
  const qs = (c && c.questionnaire) || [];
  const byId = new Map(qs.map((q) => [q.id, q]));
  const answered = new Set(((((c && c.questionnaire_answers) || {}).items) || [])
    .filter((a) => !isRequiredText(byId.get(a.id)) || String(a.answer == null ? '' : a.answer).trim())
    .map((a) => a.id));
  return qs
    .filter((q) => !q.requiresPhoto || hasPhoto)
    .filter((q) => !answered.has(q.id));
}

// Fill a {placeholder} in a prompt from an earlier answer's value (e.g. {usual_location}).
export function resolvePrompt(prompt, answers) {
  return (prompt || '').replace(/\{(\w+)\}/g, (_, key) => {
    const a = answers && answers[key];
    const v = a && (a.value != null ? a.value : (a.label != null ? a.label : a.text));
    return (v ? String(v) : 'delivery point').toLowerCase();
  });
}
